import type { EChartsOption } from 'echarts'
import { SfLog } from '@app/adapters/logger'
import type { GraphData } from '@app/application/report/screens/graphs-echarts/types'
import type { ChartRenderOptions } from '@app/application/report/screens/graphs-echarts/utils/build-chart-option'
import { EchartsHost } from './echarts-host'

export interface EchartsInitOptions extends ChartRenderOptions {
  onSeriesClick?: (params: { seriesName?: string; name?: string }) => void
}

export class EchartsChartManager {
  private host: EchartsHost | null = null
  private element: HTMLElement | null = null

  async init(
    element: HTMLElement,
    graphData: GraphData,
    options: EchartsInitOptions = {}
  ): Promise<void> {
    const { onSeriesClick, ...renderOptions } = options
    // Reuse existing chart if already initialized on the same element
    const existing = this.host?.instance
    if (existing && this.element === element) {
      const option = await this.buildOption(graphData, renderOptions)
      existing.setOption(option, true)
      this.attachClickHandler(onSeriesClick)
      return
    }

    this.dispose()
    this.element = element
    const host = new EchartsHost()
    this.host = host

    try {
      const chart = await host.mount(element)
      if (!chart) return
      const option = await this.buildOption(graphData, renderOptions)
      if (host !== this.host) return
      chart.setOption(option)
      this.attachClickHandler(onSeriesClick)
    } catch (error) {
      SfLog.error('Failed to initialize ECharts:', { error })
    }
  }

  async update(
    graphData: GraphData,
    options: EchartsInitOptions = {}
  ): Promise<void> {
    const { onSeriesClick, ...renderOptions } = options
    try {
      const chart = this.host?.instance
      if (chart) {
        const option = await this.buildOption(graphData, renderOptions)
        chart.setOption(option, true)
        this.attachClickHandler(onSeriesClick)
      } else if (this.element) {
        await this.init(this.element, graphData, options)
      }
    } catch (error) {
      SfLog.error('Failed to update ECharts:', { error })
    }
  }

  private attachClickHandler(
    onSeriesClick:
      | ((params: { seriesName?: string; name?: string }) => void)
      | undefined
  ): void {
    const chart = this.host?.instance
    if (!chart) return
    chart.off('click')
    if (!onSeriesClick) return

    // echarts' click event hands us the series and category by name; the
    // renderer maps that to report rows (see sf-echarts-renderer's resolveTarget).
    chart.on('click', (params) => {
      const p = params as { seriesName?: string; name?: string }
      onSeriesClick({ seriesName: p.seriesName, name: p.name })
    })
  }

  dispose(): void {
    this.host?.dispose()
    this.host = null
    this.element = null
  }

  private async buildOption(
    graphData: GraphData,
    options: ChartRenderOptions
  ): Promise<EChartsOption> {
    const { buildChartOption } = await import(
      /* webpackChunkName: "echarts" */ '@app/application/report/screens/graphs-echarts/utils/build-chart-option'
    )
    return buildChartOption(graphData, options)
  }
}
